import { FolderDescriptionEditor } from '@/components/folders/folder-description-editor';
import { formatNoteDate } from '@/lib/note-format';
import { cn } from '@/lib/utils';
import type { MatrixFolder } from '@mss_creator/shared';
import { Icon } from '@pms-digital/design-system';
import { useLayoutEffect, useRef, useState } from 'react';

export interface FolderDescriptionProps {
  folder: MatrixFolder;
  companyId: string;
  /** The description is being edited in place. */
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
  /** The clock of the relative date; tests pin it. */
  now?: Date;
}

/**
 * The description under the title of an open folder (issue #19, planche 15):
 * three lines at most with « Voir plus » beyond, « Modifié par X, date », and
 * a pencil that edits it in place. A folder without description offers
 * « Ajouter une description ». Anyone who sees the folder may edit it.
 */
export function FolderDescription({
  folder,
  companyId,
  editing,
  onEditingChange,
  now,
}: FolderDescriptionProps) {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the text is not read in the body, but the clamp must be measured again whenever it changes.
  useLayoutEffect(() => {
    const element = textRef.current;
    if (!element || expanded) return;
    setOverflowing(element.scrollHeight > element.clientHeight + 1);
  }, [folder.description, expanded, editing]);

  if (editing) {
    return (
      <FolderDescriptionEditor
        folder={folder}
        companyId={companyId}
        onDone={() => onEditingChange(false)}
      />
    );
  }

  if (!folder.description) {
    return (
      <button
        type="button"
        onClick={() => onEditingChange(true)}
        className="mt-1 rounded-sm text-bleu-nuit text-caption underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
      >
        Ajouter une description
      </button>
    );
  }

  return (
    <div className="mt-1 text-caption text-gray-700 leading-snug">
      <p
        ref={textRef}
        className={cn('whitespace-pre-line break-words', !expanded && 'line-clamp-3')}
      >
        {folder.description}
      </p>
      <p className="mt-1 flex flex-wrap items-center gap-x-2 text-caption text-gray-500">
        {(overflowing || expanded) && (
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((open) => !open)}
            className="rounded-sm font-semibold text-bleu-nuit underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
          >
            {expanded ? 'Voir moins' : 'Voir plus'}
          </button>
        )}
        <button
          type="button"
          aria-label="Modifier la description"
          title="Modifier la description"
          onClick={() => onEditingChange(true)}
          className="rounded-sm p-0 text-gray-600 hover:text-noir focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
        >
          <Icon name="edit" size={14} />
        </button>
        {folder.descriptionUpdatedBy && folder.descriptionUpdatedAt && (
          <span>
            Modifié par {folder.descriptionUpdatedBy.displayName},{' '}
            {formatNoteDate(folder.descriptionUpdatedAt, now)}
          </span>
        )}
      </p>
    </div>
  );
}
